import { useState, useEffect, forwardRef, useImperativeHandle } from 'react';

import { usePrevious } from '@nf/utils-common/hooks';

type RatingIconType = string;

interface StarRatingProps {
	/**
	 * How many stars do you need
	 */
	amount?: number;
	/**
	 * Default score
	 */
	score?: number;
	/**
	 * When set to true, the minimum possible rating value is 0
	 */
	isZeroBased?: boolean;
	/**
	 * Should the score be displayed
	 */
	showScore?: boolean;
	/**
	 * Icon style for each star
	 */
	iconType?: RatingIconType;
	/**
	 * class name for container
	 */
	className?: string;
	/**
	 * class name for the item of star
	 */
	starClassName?: string;
	/**
	 * Callback will be triggered when any star is clicked
	 * 
	 * @param score - the score of rating
	 */
	onRating?: (score: number) => void;
}

interface StarProps {
	/**
	 * Icon style for each star
	 */
	iconType: RatingIconType;
	/**
	 * class name for the item of star
	 */
	starClassName?: string;
	/**
	 * The no for each star
	 */
	starNo: number;
	isSelected: boolean;
	/**
	 * Should the score be displayed
	 */
	showScore: boolean;
	/**
	 * Callback will be triggered when this star component is clicked
	 * 
	 * @param starNo - the no of this star component
	 */
	onClick: (starNo: number) => void;
}

export const StarRating = forwardRef<
	any, 
	StarRatingProps
>(function StarRating(
	{
		amount = 5,
		score = 0,
		isZeroBased = false,
		showScore = false,
		iconType = '',
		className = 'survey-rank__row',
		starClassName = 'survey-star',
		onRating
	}, 
	ref
) {
	const [rating, setRating] = useState(score);
	const prevScore = usePrevious(score);

	useImperativeHandle(ref, () => ({
		getRating: () => rating
	}));

	useEffect(() => {
		if (score !== prevScore) {
			setRating(score);
		}
	}, [score, prevScore]);

	const rate = (ratingScore: number) => {
		const isCanceled = rating === ratingScore;
		const finalScore = isCanceled 
			? isZeroBased 
				? -1 
				: 0
			: ratingScore;
		setRating(finalScore);
		onRating?.(finalScore);
	};

	return (
		<div 
			className={className}
			role="radiogroup"
		>
			{Array.from({ length: amount }, (v, idx) => (
				<Star 
					key={`star-${idx}`} 
					iconType={iconType} 
					starClassName={starClassName}
					starNo={isZeroBased ? idx : idx + 1} 
					isSelected={isZeroBased 
						? idx < rating + 1 
						: idx < rating
					}
					showScore={showScore}
					onClick={rate} 
				/>
			))}
		</div>
	);
});

const Star = ({ 
	iconType, 
	starClassName,
	starNo, 
	isSelected, 
	showScore,
	onClick 
}: StarProps) => {
	const handleClick = () => onClick(starNo);

	return (
		<div 
			className={starClassName}
			// role="radio"
			aria-checked={isSelected}
			data-selected={isSelected}
			onClick={handleClick}
		>
			{showScore ? <span>{starNo}</span> : null}
			<span className={`${starClassName}__icon-${iconType}`}></span>
		</div>
	);
};